import React from 'react';
import { Col, Row, UploadFile } from 'antd';
import { Formik, Form } from 'formik';
import COButton from '../COButton';
import styles from './CreateProductModal.module.scss';
import Error from '../../components/Error';
import CircelUploadComponent from '../CircleUploadComponent';
import CreateProductFields, { Field } from './CreateProductFields';
import CreateBrandModal from '../CreateBrandModal';
import CreateProductValidationModal from './ValidationModal';
import {
  CreateProductModalButtonLabels,
  CreateProductModalProps,
} from 'enums/CreateProductModalEnums';
import { ProductModalFormValue } from '.';
import { RcFile } from 'antd/lib/upload';

export interface CreateProductModalType {
  showModalState: boolean;
  handleModalCancel: () => void;
  handleModalConfirm: (payload: any) => void;
  callbackAfterSubmit?: () => void;
  userId: number;
  isCreate: boolean;
  parentProductId?: string;
  heading?: CreateProductModalProps;
}

export interface CategoryOptions {
  value: string | number | undefined;
  label: string | undefined;
  key: string | number | undefined;
}

export interface ProductModalFormProps {
  initialValues: ProductModalFormValue;
  handleFormSubmission: (values: ProductModalFormValue) => Promise<void>;
  validationSchema: unknown;
  validate: (
    value: ProductModalFormValue,
    image: RcFile | undefined
  ) => Record<string, string> | void;
  image: RcFile | undefined;
  fileList: UploadFile[];
  setImage: (fielName?: RcFile) => void;
  isDisabled: boolean;
  fields: Field[];
  processingFacilityFields: Field[];
  activeYears: number[];
  buttonLoading: boolean;
  parsedForm: Record<string, unknown>;
  handleModalCancel: () => void;
  createBrandModalState: boolean;
  handleCloseCreateBrandModal: () => void;
  fetchBrands: (params: { size: number }) => Promise<void>;
  createBrand: (data: any) => Promise<unknown>;
  isValidationVisible: boolean;
  toggleValidationModal: () => void;
  handleRedirectToPf: (pfId: string) => () => void;
}

const ProductModalForm: React.FC<ProductModalFormProps> = ({
  initialValues,
  handleFormSubmission,
  validationSchema,
  validate,
  image,
  fileList,
  setImage,
  isDisabled,
  fields,
  processingFacilityFields,
  activeYears,
  buttonLoading,
  parsedForm,
  handleModalCancel,
  createBrandModalState,
  handleCloseCreateBrandModal,
  fetchBrands,
  createBrand,
  isValidationVisible,
  toggleValidationModal,
  handleRedirectToPf,
}) => {
  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleFormSubmission}
      enableReinitialize
      validationSchema={validationSchema}
      validate={value => validate(value, image)}>
      {formik => (
        <Form>
          <Row className={styles.fieldContainer}>
            <div className={styles.uploadContainer}>
              <div>
                <CircelUploadComponent
                  fileListData={fileList}
                  setImage={setImage}
                  isDisabled={isDisabled}
                  isFormik
                />
                <Error message={formik?.errors?.image as string} />
              </div>
            </div>
            <Col span={24}>
              <CreateProductFields fields={fields} />
            </Col>
            <Col span={24} className={styles.subTitle}>
              Processing Facility
            </Col>
            <Col span={24}>
              <CreateProductFields
                fields={processingFacilityFields}
                activeYears={activeYears}
              />
            </Col>
          </Row>
          {!isDisabled && (
            <div className={styles.buttonContainer}>
              <div className={styles.singleButtonContainer}>
                <COButton loading={buttonLoading} submit>
                  {parsedForm?.id
                    ? CreateProductModalButtonLabels.UPDATE
                    : CreateProductModalButtonLabels.CREATE}
                </COButton>
              </div>
              <div className={styles.singleButtonContainer}>
                <COButton secondary onClick={handleModalCancel}>
                  {CreateProductModalButtonLabels.CANCEL}
                </COButton>
              </div>
            </div>
          )}
          <CreateBrandModal
            showModalState={createBrandModalState}
            handleModalCancel={handleCloseCreateBrandModal}
            successCallback={async brandId => {
              await fetchBrands({ size: 250 });
              formik.setFieldValue('brand', brandId);
            }}
            createBrand={createBrand}
          />
          <CreateProductValidationModal
            isOpen={isValidationVisible}
            onClose={toggleValidationModal}
            handleUpdate={handleRedirectToPf(
              String(formik.values.processingFacility || '')
            )}
          />
        </Form>
      )}
    </Formik>
  );
};

export default ProductModalForm;
